<script setup lang="ts">
import { ref, watch } from 'vue';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CLoader from '@/components/shared/CLoader.vue';
import { getStylePresets, resolveThumbnailUrl } from '../../core/presets';
import defaultThumbnail from '@/assets/images/crossword-default-thumb.png';
import type { CrosswordStyleDraft } from '../../types';

const props = defineProps<{
    value: boolean;
    addedPresetIds?: string[];
}>();

const emit = defineEmits<{
    (event: 'input', value: boolean): void;
    (event: 'select', value: CrosswordStyleDraft): void;
    (event: 'create-custom'): void;
}>();

const presets = ref<CrosswordStyleDraft[]>([]);
const loading = ref(false);

const loadPresets = async () => {
    if (presets.value.length || loading.value) return;

    loading.value = true;
    try {
        presets.value = await getStylePresets();
    } finally {
        loading.value = false;
    }
};

const thumbnailUrl = (preset: CrosswordStyleDraft) => resolveThumbnailUrl(preset) || defaultThumbnail;

const isAdded = (preset: CrosswordStyleDraft) => !!props.addedPresetIds?.includes(preset.presetId || '');

const handleSelect = (preset: CrosswordStyleDraft) => {
    if (isAdded(preset)) return;
    emit('select', preset);
};

watch(
    () => props.value,
    (isOpen) => {
        if (isOpen) loadPresets();
    },
    { immediate: true }
);
</script>

<template>
    <c-modal
        :value="value"
        title="Select a Crossword Style"
        size="xl"
        hide-footer
        @input="emit('input', $event)"
        @closed="emit('input', false)"
    >
        <c-loader v-model="loading" :full-size="false" text="Loading styles..." />
        <div class="styles-grid">
            <c-button variant="empty" class="style-thumbnail p-0" @click="emit('create-custom')">
                <img :src="require('@/angular-app/images/template/crossword-custom-style.png')" alt="Custom Style" />
                <span>Custom Style</span>
            </c-button>
            <c-button
                v-for="preset in presets"
                :key="preset.presetId"
                variant="empty"
                class="style-thumbnail p-0"
                :class="{ added: isAdded(preset) }"
                :disabled="isAdded(preset)"
                @click="handleSelect(preset)"
            >
                <img :src="thumbnailUrl(preset)" :alt="preset.name" />
                <span>{{ preset.name }}</span>
            </c-button>
        </div>
    </c-modal>
</template>

<style scoped lang="scss">
@import 'styles/utilities/functions';

// The loader overlays this container, so it needs room while the presets load
.styles-grid {
    min-height: 8rem;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
    gap: 1rem;
    max-height: 25rem;
    overflow-y: auto;
    align-items: start;
}

.style-thumbnail {
    // .btn wins the cascade with its inline-block, so the column layout has to be forced
    display: flex !important;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    white-space: normal;
    background: none;

    img {
        width: 100%;
        aspect-ratio: 1;
        object-fit: cover;
        border-radius: 4px;
        border: 1px solid get-color('gray-light');
    }

    span {
        width: 100%;
        font-size: 0.8125rem;
        line-height: 1.15;
        text-align: left;
        // Reserve two lines so labels that wrap don't push the row out of line
        min-height: 2.3em;
    }

    &:hover {
        background-color: transparent !important;
    }

    &:hover:not(.added) img {
        border-color: transparent;
        background-color: get-color('gray-light') !important;
        padding: 0.125rem;
    }

    &.added {
        opacity: 0.5;
    }
}
</style>
